import { memo, useState, useEffect, useCallback } from 'react';
import { Text, Chip } from '@Sage/hoi-poi-ui';

import { getEntityValue } from 'services/Fuzzy';
import { formatDate } from 'utils/dates';
import { getLiteral } from 'utils/getLiteral';
import { FuzzyMap } from 'utils/fuzzy';
import { getDayWeekOptions } from '../../../../utils';

const ActionBoxValue = memo(({ item, entityFieldsMap }) => {
    const getValueType = useCallback(
        (value) => {
            switch (item?.field?.type) {
                case 'percentage':
                    return `${value}%`;
                case 'datetime':
                    return formatDate(value, 'L HH:mm');
                case 'bit':
                    return value ? getLiteral('cfm_label_yes') : getLiteral('cfm_label_no');
                default:
                    return value;
            }
        },
        [item?.field?.type],
    );

    const [innerValue, setInnerValue] = useState(
        ['list', 'multivalue'].includes(item?.field?.type) ? (
            ''
        ) : (
            <Text isTruncated useTooltip>
                {getValueType(item?.value?.value)}
            </Text>
        ),
    );

    useEffect(() => {
        const value = item?.value?.value;
        if (value === undefined || value === null || value === '') {
            setInnerValue();
            return;
        }

        switch (item?.value?.type) {
            case 'calculated_calculator_int':
            case 'calculated_calculator_float':
            case 'calculated_string':
                // Parse the string to find variables in {{ variable }} format and render them as chips
                const parseStringWithVariables = (str) => {
                    const parts = [];
                    let lastIndex = 0;
                    const variableRegex = /\{\{([^}]+)\}\}/g;
                    let match;

                    while ((match = variableRegex.exec(str)) !== null) {
                        // Add text before the variable
                        if (match.index > lastIndex) {
                            parts.push(str.substring(lastIndex, match.index));
                        }

                        // Add the variable as a chip
                        const variableKey = match[1]?.trim();
                        const fieldInfo = entityFieldsMap[variableKey];
                        if (fieldInfo) {
                            parts.push(
                                <Chip key={match.index} icon={fieldInfo.icon} isFilled>
                                    {fieldInfo.label}
                                </Chip>,
                            );
                        } else {
                            // Fallback if field not found in map
                            parts.push(
                                <Chip key={match.index} isFilled>
                                    {variableKey}
                                </Chip>,
                            );
                        }

                        lastIndex = match.index + match[0].length;
                    }

                    // Add remaining text after the last variable
                    if (lastIndex < str.length) {
                        parts.push(str.substring(lastIndex));
                    }

                    return parts;
                };

                setInnerValue(
                    <div className="fm-automation-builder__chip-values">
                        {parseStringWithVariables(value).map((part, index) =>
                            typeof part === 'string' ? <Text key={index}>{part}</Text> : part,
                        )}
                    </div>,
                );
                return;
            case 'calculated_datetime':
            case 'calculated_boolean':
            case 'calculated_single_list':
            case 'calculated_multi_list':
                // Extract the field key from the value (template format)
                const fieldKey = value?.value?.replace(/\{\{\s*|\s*\}\}/g, '')?.trim();
                const fieldInfo = entityFieldsMap[fieldKey];
                const label = fieldInfo?.label || value?.label;
                const icon = fieldInfo?.icon;

                setInnerValue(
                    <Chip isFilled icon={icon}>
                        {label}
                    </Chip>,
                );
                return;
            case 'days_after_datetime':
            case 'week_after_datetime':
            case 'month_after_datetime':
                // Extract the field key from the value (template format)
                const dateFieldKey = value[0]?.value?.replace(/\{\{\s*|\s*\}\}/g, '')?.trim();
                const dateFieldInfo = entityFieldsMap[dateFieldKey];
                const dateFieldLabel = dateFieldInfo?.label || value[0]?.label;

                setInnerValue(
                    <Text isTruncated useTooltip>
                        {value[1]} - {dateFieldLabel}{' '}
                        {value[2] ? `(${getLiteral('action_skip_weekends')})` : null}
                    </Text>,
                );
                return;
            case 'next_day_week_datetime':
                const options = getDayWeekOptions();
                // Extract the field key from the value (template format)
                const weekFieldKey = value[0]?.value?.replace(/\{\{\s*|\s*\}\}/g, '')?.trim();
                const weekFieldInfo = entityFieldsMap[weekFieldKey];
                const weekFieldLabel = weekFieldInfo?.label || value[0]?.label;

                setInnerValue(
                    <Text isTruncated useTooltip>
                        {options.find((o) => o.value === value[1])?.label} - {weekFieldLabel}
                    </Text>,
                );
                return;
        }

        if (!['list', 'multivalue'].includes(item?.field?.type)) {
            setInnerValue(
                <Text isTruncated useTooltip>
                    {getValueType(value)}
                </Text>,
            );
            return;
        }

        const valueArray = Array.isArray(item?.value?.value)
            ? item?.value?.value
            : [item?.value?.value];

        const list =
            FuzzyMap[item?.field?.table?.table?.toLowerCase()]?.list || item?.field?.table?.table;

        getEntityValue(list, valueArray)
            .then((result) => {
                if (!result?.data) return;
                const resultValue = valueArray?.map((v) => ({
                    label: result?.data?.[v]?.description,
                    value: result?.data?.[v]?.id,
                }));

                setInnerValue(
                    <div className="fm-automation-builder__action-box__chips">
                        {resultValue.map((v) => (
                            <Chip key={v.value} isFilled>
                                {v.label}
                            </Chip>
                        ))}
                    </div>,
                );
            })
            .catch((e) => {
                console.error(e);
                setInnerValue();
            });
    }, [
        entityFieldsMap,
        getValueType,
        item,
        item?.field?.table?.table,
        item?.field?.type,
        item?.value?.type,
        item?.value?.value,
    ]);

    return innerValue;
});

export default ActionBoxValue;
